import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Zap, RotateCcw, ChevronRight, CheckCircle, Clock, Shield, Hash, Truck, FileText, Package, AlertCircle } from 'lucide-react';
import { useAdminAuth } from '../AuthContext';
import { adminDemoStatus, adminDemoSeed, adminDemoReset } from '../../services/api';

const PIPELINE_STAGES = [
  { key: 'order_created',      label: 'Order Created',      icon: Package,     status_match: ['new'],                             href_fn: (oid) => `/admin/orders/${oid}`,    module: 'Order Detail' },
  { key: 'payment',            label: 'Payment Verified',   icon: CheckCircle, status_match: ['payment_verified'],                 href_fn: (oid) => `/admin/orders/${oid}`,    module: 'Orders' },
  { key: 'validation',         label: 'Validation Passed',  icon: CheckCircle, status_match: ['ready_for_production', 'batched'],  href_fn: (oid) => `/admin/orders/${oid}`,    module: 'Order Detail' },
  { key: 'production',         label: 'Production Plan',    icon: Zap,         status_match: ['batched', 'in_production'],         href: '/admin/production',                    module: 'Production' },
  { key: 'serialized',         label: 'Serialization',      icon: Hash,        status_match: ['serialized'],                       href: '/admin/serialization',                 module: 'Serialization' },
  { key: 'packed',             label: 'Packed & Verified',  icon: Package,     status_match: ['packed', 'ready_to_ship'],          href: '/admin/shipping',                      module: 'Shipping' },
  { key: 'shipped',            label: 'Shipped',            icon: Truck,       status_match: ['shipped'],                          href: '/admin/shipping',                      module: 'Shipping' },
];

const STAGE_EXPLANATIONS = {
  order_created:  'Dealer or public customer places an order. Order enters the pipeline at "New" status with full customer info, items, and shipping address.',
  payment:        'Payment is processed and verified. For dealer orders, this is invoice confirmation. Order advances to payment_verified status.',
  validation:     'Validation engine runs 7 checks: payment verified, shipping address complete, dealer FFL status, inventory availability, NFA compliance. Passes with warnings → moves to Ready for Production.',
  production:     'Admin generates a production cut sheet grouping this order with others by product SKU. Order is batched and moves to In Production.',
  serialized:     'Serial numbers (format: WM-SUPP-YYYY-XXXXXX) are generated and assigned to each physical unit. Immutable assignment history created.',
  packed:         'Physical items are packed. Document checklist is reviewed. Packing slip generated. Order moves to Packed status.',
  shipped:        'Shipping label generated (mock). Tracking number entered. Order marked Shipped. All serials updated to Shipped status. Immutable audit log entry created.',
};

function StageCard({ stage, isCompleted, isActive, orderId }) {
  const [expanded, setExpanded] = useState(false);
  const Icon = stage.icon;
  const href = stage.href || (stage.href_fn && orderId ? stage.href_fn(orderId) : '/admin');
  return (
    <div className={`border transition-all ${isCompleted ? 'border-green-500/30 bg-green-500/3' : isActive ? 'border-orange-500/30 bg-orange-500/3' : 'border-white/10 bg-[#1a1a1a]'}`}>
      <button onClick={() => setExpanded(!expanded)} className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-white/3 transition-colors">
        <div className={`w-8 h-8 flex items-center justify-center flex-shrink-0 border ${isCompleted ? 'border-green-500/50 bg-green-500/20' : isActive ? 'border-orange-500/50 bg-orange-500/20' : 'border-zinc-700 bg-zinc-800'}`}>
          <Icon size={15} className={isCompleted ? 'text-green-400' : isActive ? 'text-orange-400' : 'text-zinc-500'} />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className={`font-bebas text-base tracking-wider ${isCompleted ? 'text-green-400' : isActive ? 'text-white' : 'text-zinc-500'}`}>{stage.label}</span>
            {isCompleted && <CheckCircle size={12} className="text-green-400" />}
            {isActive && <span className="font-inter text-[10px] text-orange-400 border border-orange-500/20 bg-orange-500/10 px-1.5 py-0.5 uppercase tracking-wider">Active</span>}
          </div>
          <span className="font-inter text-[11px] text-zinc-600">Module: {stage.module}</span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Link to={href} onClick={e => e.stopPropagation()}
            className="flex items-center gap-1 font-inter text-xs text-orange-500 hover:text-white transition-colors"
            data-testid={`demo-go-to-${stage.key}`}
          >
            Go to <ChevronRight size={12} />
          </Link>
        </div>
      </button>
      {expanded && (
        <div className="border-t border-white/5 px-5 py-3 bg-[#0f0f0f]">
          <p className="font-inter text-sm text-zinc-400 leading-relaxed">{STAGE_EXPLANATIONS[stage.key]}</p>
        </div>
      )}
    </div>
  );
}

export default function AdminDemo() {
  const { token } = useAdminAuth();
  const BASE = process.env.REACT_APP_BACKEND_URL;
  const [demoStatus, setDemoStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [seedingOrders, setSeedingOrders] = useState(false);
  const [seedOrdersResult, setSeedOrdersResult] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = () => {
    adminDemoStatus(token).then(setDemoStatus).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [token]);

  const handleSeed = async () => {
    setSeeding(true); setError(''); setSuccess('');
    try { await adminDemoSeed(token); setSuccess('Demo data seeded! The walkthrough order is ready.'); load(); }
    catch (err) { setError(err.message); }
    setSeeding(false);
  };

  const handleReset = async () => {
    if (!window.confirm('Reset all demo data? This cannot be undone.')) return;
    setResetting(true);
    try { await adminDemoReset(token); setSuccess('Demo data cleared.'); setDemoStatus({ seeded: false, demo_orders: [] }); }
    catch (err) { setError(err.message); }
    setResetting(false);
  };

  const handleSeedOrders = async () => {
    setSeedingOrders(true); setError(''); setSeedOrdersResult(null);
    try {
      const res = await fetch(`${BASE}/api/admin/demo/seed-orders`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setSeedOrdersResult(data);
    } catch (err) { setError(err.message); }
    setSeedingOrders(false);
  };

  const handleResetSeedOrders = async () => {
    if (!window.confirm('Delete all seeded demo orders? Real orders are NOT affected.')) return;
    try {
      await fetch(`${BASE}/api/admin/demo/reset-orders`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      setSeedOrdersResult(null);
      setSuccess('Seeded demo orders cleared.');
    } catch (err) { setError(err.message); }
  };

  const demoOrder = demoStatus?.demo_orders?.[0];
  const orderId = demoOrder?._id;
  const orderStatus = demoOrder?.status || '';
  const completedStages = PIPELINE_STAGES.filter(s => s.status_match.some(sm => {
    const idx = ['new','payment_pending','payment_verified','validation_pending','ready_for_production','batched','in_production','serialized','packed','ready_to_ship','shipped'];
    return idx.indexOf(orderStatus) >= idx.indexOf(sm);
  }));

  return (
    <div data-testid="admin-demo-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-bebas text-4xl text-white tracking-wider">SYSTEM WALKTHROUGH</h1>
          <p className="font-inter text-sm text-zinc-500 mt-1">Demo mode — full end-to-end pipeline demonstration</p>
        </div>
        <div className="flex gap-3">
          {demoStatus?.seeded && (
            <button
              data-testid="demo-reset-button"
              onClick={handleReset}
              disabled={resetting}
              className="flex items-center gap-2 font-inter text-sm border border-red-500/30 text-red-400 hover:border-red-500/60 hover:bg-red-500/10 px-4 py-2.5 transition-colors disabled:opacity-40"
            >
              <RotateCcw size={14} />{resetting ? 'Resetting...' : 'Reset Demo'}
            </button>
          )}
          <button
            data-testid="demo-seed-button"
            onClick={handleSeed}
            disabled={seeding || (demoStatus?.seeded && demoStatus.demo_orders.length > 0)}
            className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-inter text-sm font-medium px-4 py-2.5 transition-colors"
          >
            <Zap size={14} className={seeding ? 'animate-pulse' : ''} />
            {seeding ? 'Seeding...' : demoStatus?.seeded ? 'Demo Active' : 'Seed Demo Data'}
          </button>
        </div>
      </div>

      {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 font-inter text-sm px-4 py-3 mb-4">{error}</div>}
      {success && <div className="bg-green-500/10 border border-green-500/30 text-green-400 font-inter text-sm px-4 py-3 mb-4">{success}</div>}

      {/* ── SEED ORDERS PANEL ─────────────────────────────────────── */}
      <div className="bg-[#1a1a1a] border border-orange-500/20 p-5 mb-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h3 className="font-bebas text-xl text-white tracking-wider mb-1 flex items-center gap-2">
              <Package size={16} className="text-orange-500" />POPULATE ORDER PIPELINE
            </h3>
            <p className="font-inter text-xs text-zinc-400 max-w-lg">
              Seeds 16 new demo orders (8 dealer + 8 public) across all product lines — all set to <strong className="text-white">New</strong> status
              so you can run them through the complete workflow. Safe to run on production.
            </p>
            {seedOrdersResult && (
              <div className={`mt-3 font-inter text-xs px-3 py-2 border ${seedOrdersResult.already_seeded ? 'text-yellow-400 border-yellow-500/30 bg-yellow-500/10' : 'text-green-400 border-green-500/30 bg-green-500/10'}`}>
                {seedOrdersResult.already_seeded
                  ? `Already seeded — ${seedOrdersResult.count} demo orders exist. Use "Clear" to reset.`
                  : `✓ ${seedOrdersResult.orders_created} orders created — ${seedOrdersResult.dealer_orders} dealer + ${seedOrdersResult.public_orders} public. All set to "New".`}
              </div>
            )}
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <button
              data-testid="seed-orders-btn"
              onClick={handleSeedOrders}
              disabled={seedingOrders}
              className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-bebas text-base tracking-widest px-5 py-2.5 transition-colors whitespace-nowrap"
            >
              <Zap size={14} className={seedingOrders ? 'animate-pulse' : ''} />
              {seedingOrders ? 'SEEDING...' : 'SEED 16 ORDERS'}
            </button>
            <button
              onClick={handleResetSeedOrders}
              className="flex items-center gap-2 border border-red-500/30 text-red-400 hover:bg-red-500/10 font-inter text-xs px-4 py-2.5 transition-colors whitespace-nowrap"
              title="Delete seeded demo orders (real orders are safe)"
            >
              <RotateCcw size={12} />Clear
            </button>
          </div>
        </div>
      </div>
      {/* ─────────────────────────────────────────────────────────── */}

      {!demoStatus?.seeded ? (
        <div className="text-center py-16 bg-[#1a1a1a] border border-white/10">
          <Zap size={48} className="text-zinc-700 mx-auto mb-4" />
          <h2 className="font-bebas text-3xl text-zinc-500 tracking-wider mb-3">NO DEMO DATA</h2>
          <p className="font-inter text-sm text-zinc-600 mb-6 max-w-md mx-auto">
            Click "Seed Demo Data" to create a complete demo order that has gone through the entire pipeline — from order creation to shipment.
          </p>
          <button
            onClick={handleSeed}
            disabled={seeding}
            className="font-bebas text-lg tracking-widest bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 transition-colors"
          >
            {seeding ? 'SEEDING...' : 'SEED DEMO DATA'}
          </button>
        </div>
      ) : (
        <>
          {/* Demo Order Status */}
          <div className="bg-[#111111] border border-orange-500/30 p-5 mb-6">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <span className="font-inter text-xs font-bold text-orange-400 bg-orange-500/20 border border-orange-500/30 px-2 py-1 uppercase tracking-wider">DEMO</span>
                <div>
                  <div className="font-bebas text-2xl text-white tracking-wider">{demoOrder?.order_number || 'WM-DEMO-2026-001'}</div>
                  <p className="font-inter text-xs text-zinc-500">DEMO — Smith's Tactical Supply · 2 × Mod-1 Quick Disconnect (NFA)</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-inter text-[10px] font-bold text-teal-400 bg-teal-500/10 border border-teal-500/20 px-2 py-0.5 uppercase tracking-wider">
                  {orderStatus?.replace(/_/g, ' ') || 'SHIPPED'}
                </span>
                {orderId && (
                  <Link to={`/admin/orders/${orderId}`}
                    className="font-inter text-xs text-orange-500 hover:text-white transition-colors border border-orange-500/30 px-3 py-1"
                    data-testid="demo-view-order">
                    View Order →
                  </Link>
                )}
              </div>
            </div>
          </div>

          {/* Pipeline Walkthrough */}
          <div className="mb-6">
            <h2 className="font-bebas text-2xl text-white tracking-wider mb-4">PIPELINE WALKTHROUGH</h2>
            <p className="font-inter text-sm text-zinc-500 mb-4">
              Click any stage to see what happened at that point. Use the "Go to" links to navigate directly to the relevant admin module.
            </p>
            <div className="space-y-2">
              {PIPELINE_STAGES.map((stage, i) => {
                const isCompleted = completedStages.some(s => s.key === stage.key) || orderStatus === 'shipped';
                const isActive = !isCompleted && i === completedStages.length;
                return (
                  <StageCard key={stage.key} stage={stage} isCompleted={isCompleted || orderStatus === 'shipped'}
                    isActive={isActive} orderId={orderId} />
                );
              })}
            </div>
          </div>

          {/* Quick Navigation */}
          <div>
            <h2 className="font-bebas text-2xl text-white tracking-wider mb-4">QUICK NAVIGATION</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {[
                { label: 'Order Detail', href: orderId ? `/admin/orders/${orderId}` : '/admin/orders', icon: Package, desc: 'Full order info, validation, payment' },
                { label: 'Serialization', href: '/admin/serialization', icon: Hash, desc: 'Serial trace view, assignment history' },
                { label: 'Shipping', href: '/admin/shipping', icon: Truck, desc: 'Packing slip, shipping label' },
                { label: 'Production', href: '/admin/production', icon: Zap, desc: 'Cut sheet, production plan' },
                { label: 'Audit Log', href: '/admin/audit', icon: FileText, desc: 'Immutable event ledger' },
                { label: 'Orders Pipeline', href: '/admin/orders', icon: CheckCircle, desc: 'All orders + status filters' },
              ].map(n => {
                const Icon = n.icon;
                return (
                  <Link key={n.href} to={n.href} data-testid={`demo-nav-${n.label.toLowerCase().replace(/\s/g,'-')}`}
                    className="bg-[#1a1a1a] border border-white/10 hover:border-orange-500/40 p-4 transition-all group">
                    <Icon size={18} className="text-orange-500 mb-2 group-hover:scale-110 transition-transform" />
                    <p className="font-bebas text-base text-white tracking-wide">{n.label}</p>
                    <p className="font-inter text-[11px] text-zinc-500 mt-0.5 leading-tight">{n.desc}</p>
                  </Link>
                );
              })}
            </div>
          </div>

          {/* System Philosophy */}
          <div className="mt-6 bg-[#111111] border border-white/10 p-6">
            <h3 className="font-bebas text-xl text-white tracking-wider mb-3">SYSTEM ARCHITECTURE SUMMARY</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { title: 'State Machine', desc: '13 pipeline states with enforced transitions. Invalid moves are rejected with helpful error messages. Every transition is logged.' },
                { title: 'Immutable Audit', desc: 'All events append-only to audit_log. No deletes. Full traceability per serial number, order, shipment, and payment.' },
                { title: 'Payment Abstraction', desc: 'Provider-agnostic. Currently: mock (public) + manual_invoice (dealer). Ready to plug in Stripe, Authorize.Net, or any processor.' },
              ].map(s => (
                <div key={s.title} className="bg-[#1a1a1a] border border-white/5 p-4">
                  <p className="font-bebas text-base text-orange-500 tracking-wider mb-1">{s.title}</p>
                  <p className="font-inter text-xs text-zinc-400 leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
